import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { Service } from '@magicdoorinc/env';
import { auth, isShadowSession } from '~/config/auth';
import { envConfig } from '~/utils/env';

const presenceUrl = `${envConfig.service[Service.Auth].replace(/\/$/, '')}/presence/heartbeat`;

/**
 * Build an auth header from the token ALREADY in storage, without ever calling
 * `getAuthHeader()`/`getAccessToken()` — those trigger a refresh. Presence is
 * analytics; it must never drive session lifecycle. So we read the stored token
 * and bail (sending nothing) if it's missing or expired, letting the normal
 * refresh path own renewal instead.
 */
const stillValidAuthHeader = (): { Authorization: string } | null => {
  const { access_token: token, token_type: tokenType, token_expiry: expiry } = auth.storage.getTokenStorage();
  if (!token || !tokenType) return null;
  // Expired (or unparseable) -> skip this ping rather than refresh. Mirrors the
  // expiry check in auth.ts's isAccountExpired.
  if (!expiry || !todayWithSystemTimeZone().isBefore(expiry)) return null;
  return { Authorization: `${tokenType} ${token}` };
};

export const sendHeartbeat = async (currentRoute?: string, extra?: Record<string, unknown>): Promise<void> => {
  if (isShadowSession()) return;

  // Don't ping from a backgrounded tab — combined with refresh-on-focus, the tab
  // does nothing auth-touching while hidden and renews cleanly on return.
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

  const authHeaders = stillValidAuthHeader();
  if (!authHeaders) return;

  try {
    await fetch(presenceUrl, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
      headers: { 'content-type': 'application/json', ...authHeaders },
      body: JSON.stringify({ currentRoute, extra }),
    });
  } catch {
    return;
  }
};
